import React, { useEffect, useMemo, useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { motion } from "framer-motion";
import { getApiBase, resolveAsset } from "../utils/paths";
import "./EventsReports.css";

const eventCategories = ["All", "Education", "Health", "Empowerment", "Environment", "Community"];

const resolveImage = (path) => {
  if (!path) return resolveAsset("assets/images/backgrounds/page-header-bg.jpg");
  if (/^https?:\/\//i.test(path)) return path;
  const backendBase = getApiBase().replace(/\/backend\/api$/, "/backend");
  return path.startsWith("/backend")
    ? `${backendBase}${path.replace(/^\/backend/, "")}`
    : path.startsWith("/uploads")
    ? `${backendBase}${path}`
    : `${backendBase}/${path}`;
};

const EventsReports = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const pageSize = 6;
  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / pageSize));

  useEffect(() => {
    let active = true;
    const loadEvents = async () => {
      setLoading(true);
      try {
        const apiBase = getApiBase();
        const res = await fetch(`${apiBase}/public/events.php`);
        if (!res.ok) throw new Error("Unable to load events");
        const data = await res.json();
        if (!active) return;
        setEvents(Array.isArray(data.items) ? data.items : []);
      } catch (error) {
        console.error("Failed to fetch events", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadEvents();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = events.filter((event) => {
      const title = event.title?.toLowerCase() || "";
      const description = event.description?.toLowerCase() || "";
      const venue = event.venue?.toLowerCase() || "";
      const place = event.place?.toLowerCase() || "";
      const categoryMatch =
        selectedCategory === "All" ||
        event.place?.toLowerCase() === selectedCategory.toLowerCase();
      const searchMatch =
        normalizedSearch === "" ||
        title.includes(normalizedSearch) ||
        description.includes(normalizedSearch) ||
        venue.includes(normalizedSearch) ||
        place.includes(normalizedSearch);
      return categoryMatch && searchMatch;
    });
    setFilteredEvents(filtered);
    setCurrentPage(1);
  }, [events, searchTerm, selectedCategory]);

  const pageEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredEvents.slice(startIndex, startIndex + pageSize);
  }, [filteredEvents, currentPage]);

  return (
    <>
      <Header />

      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: `url(${resolveAsset("assets/images/backgrounds/page-header-bg.jpg")})`,
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src={resolveAsset("assets/images/shapes/page-header-shape1.png")} alt="" />
        </div>
        <div className="shape2">
          <img src={resolveAsset("assets/images/shapes/page-header-shape2.png")} alt="" />
        </div>
        <div className="container">
          <div className="page-header__inner">
            <motion.h2 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              Upcoming Events
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} style={{ color: "#dad2d0", fontSize: "24px", marginTop: "20px" }}>
              Explore our latest community programs, workshops, and volunteer opportunities.
            </motion.p>
           
          </div>
        </div>
      </section>

      <section className="event-filter-section events-reports-filter py-5">
        <div className="container">
          <div className="row gy-3 align-items-end">
            <div className="col-lg-5">
              <label htmlFor="event-search" className="form-label">Search Events</label>
              <input
                id="event-search"
                type="search"
                className="form-control"
                placeholder="Search by title, place, or venue"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="col-lg-3 text-lg-end">
              <div className="text-secondary mt-2">{filteredEvents.length} event{filteredEvents.length === 1 ? "" : "s"} found</div>
            </div>
          </div>
        </div>
      </section>

      <section className="event-cards-section events-reports-cards pb-5">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">Loading events...</div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-5">No upcoming events found.</div>
          ) : (
            <div className="row g-4">
              {pageEvents.map((event) => (
                <div key={event.id} className="col-xl-4 col-lg-6">
                  <motion.div
                    className="card shadow-sm border-0 h-100"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.55 }}
                  >
                    <div className="position-relative overflow-hidden" style={{ minHeight: "240px" }}>
                      <img src={resolveImage(event.image_url)} alt={event.title} className="w-100 h-100" style={{ objectFit: "cover" }} />
                      <div className="position-absolute top-0 start-0 bg-primary text-white px-3 py-2 rounded-end">
                        <strong>{new Date(event.event_date).toLocaleString("en-US", { month: "short" })}</strong>
                        <div>{new Date(event.event_date).getDate()}</div>
                      </div>
                    </div>
                    <div className="card-body">
                      <span className="text-primary small text-uppercase fw-semibold">{event.place || "Community"}</span>
                      <h3 className="h5 mt-3">{event.title}</h3>
                      <p className="text-muted">{event.description?.slice(0, 110)}{event.description?.length > 110 ? "..." : ""}</p>
                      <ul className="list-unstyled mb-4 small text-muted">
                        <li><strong>Time:</strong> {event.event_time || "TBA"}</li>
                        <li><strong>Venue:</strong> {event.venue || "TBA"}</li>
                        <li><strong>Location:</strong> {event.place || "Bhubaneswar"}</li>
                      </ul>
                      {/* <a href="#" className="btn btn-primary btn-sm">Register Now</a> */}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          )}

          {filteredEvents.length > pageSize && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-5 flex-wrap">
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  className={`btn btn-sm ${currentPage === index + 1 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</button>
            </div>
          )}
        </div>
      </section>

      

      <Footer />
      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-target__wrapper"><span className="scroll-to-top__inner" /></span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default EventsReports;
